/**
 * Mail to the salesperson of a customer (E15). `EmailComposerModal` is bound to the inventory contexts and its own
 * endpoints, so the credit-limits page composes here with `creditLimitEmailTemplate` and its own two endpoints
 * (`emails/suggest-recipients`, `emails/send`; both stay, masterplan §4). The salesperson is suggested, others can be
 * searched; the template's table, comments and footer are fixed, the intro is editable.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../ui/Toast';
import { creditLimitsApi } from '../../../services/creditLimitsApi';
import { buildCreditLimitTemplate } from '../../email/creditLimitEmailTemplate';
import type { CreditLimitComment, OverviewRow } from '../../../types/creditLimits';
import { OverviewDialog } from './OverviewDialog';

interface Person { email: string; name: string }
interface Suggestions { suggestedSalesManager: Person | null; allActiveEmployees: Person[] }

export function EmailDialog({ row, onClose }: { row: OverviewRow; onClose: () => void }) {
    const { user } = useAuth();
    const { toast } = useToast();
    const senderEmail = user?.email ?? '';

    const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
    const [comments, setComments] = useState<CreditLimitComment[]>([]);
    // The lookups run together; a failed one is shown, never swallowed (CLAUDE.md, «empty dropdown»).
    const [lookupErrors, setLookupErrors] = useState<string[]>([]);
    const [to, setTo] = useState<Person[]>([]);
    const [search, setSearch] = useState('');
    const [subject, setSubject] = useState('');
    const [intro, setIntro] = useState('');
    const [sending, setSending] = useState(false);

    const template = useMemo(() => buildCreditLimitTemplate(row, comments, senderEmail), [row, comments, senderEmail]);

    useEffect(() => {
        let cancelled = false;
        Promise.allSettled([
            creditLimitsApi.suggestRecipients(row.customerCode),
            creditLimitsApi.getComments(row.customerCode),
        ]).then(([sug, com]) => {
            if (cancelled) return;
            const errors: string[] = [];
            if (sug.status === 'fulfilled') {
                setSuggestions(sug.value);
                const sm = sug.value.suggestedSalesManager;
                if (sm) setTo(prev => (prev.length === 0 ? [sm] : prev));
            } else {
                errors.push(`Recipients: ${sug.reason instanceof Error ? sug.reason.message : 'failed to load'}`);
            }
            if (com.status === 'fulfilled') setComments(com.value);
            else errors.push(`Comments: ${com.reason instanceof Error ? com.reason.message : 'failed to load'}; the mail goes without them.`);
            setLookupErrors(errors);
        });
        return () => { cancelled = true; };
    }, [row.customerCode]);

    // The template fills subject and intro once, on the first load; a field the person emptied on purpose stays empty
    // when the comments arrive and the template is rebuilt.
    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current) return;
        seeded.current = true;
        setSubject(template.subject);
        setIntro(template.plainIntro);
    }, [template]);

    const results = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!suggestions || q.length < 2) return [];
        return suggestions.allActiveEmployees
            .filter(p => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
            .slice(0, 12);
    }, [suggestions, search]);

    const has = (p: Person) => to.some(x => x.email.toLowerCase() === p.email.toLowerCase());
    const add = (p: Person) => { if (!has(p)) setTo(prev => [...prev, p]); setSearch(''); };
    const remove = (p: Person) => setTo(prev => prev.filter(x => x.email.toLowerCase() !== p.email.toLowerCase()));

    const send = async () => {
        if (to.length === 0 || !subject.trim()) return;
        setSending(true);
        try {
            const result = await creditLimitsApi.sendEmail({
                to: to.map(p => ({ email: p.email, name: p.name })),
                cc: senderEmail ? [{ email: senderEmail }] : [],
                subject,
                htmlBody: template.buildHtmlBody(intro),
                plainBodyPreview: intro,
                contextType: 'CreditLimitReview',
                contextKey: row.customerCode,
                contextPayload: { customerCode: row.customerCode, salesManagerName: row.salesManagerName },
            });
            toast(`Email sent (log #${result.emailLogId ?? '?'})`, 'success');
            onClose();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to send email', 'error');
        } finally {
            setSending(false);
        }
    };

    return (
        <OverviewDialog
            title="Email to salesperson"
            subtitle={`${row.customerName ?? row.customerCode} (${row.customerCode})`}
            onClose={onClose}
            width={760}
            footer={(
                <>
                    <span className="muted" style={{ marginRight: 'auto' }}>Sent as {senderEmail || 'you'}; you are on Cc.</span>
                    <button type="button" className="btn secondary" onClick={onClose} disabled={sending}>Cancel</button>
                    <button type="button" className="btn primary" onClick={send} disabled={sending || to.length === 0 || !subject.trim()}>
                        {sending ? 'Sending…' : 'Send'}
                    </button>
                </>
            )}
        >
            <div className="clim-panel">
                {lookupErrors.map(e => <div key={e} className="banner error" role="alert"><p>{e}</p></div>)}

                <div>
                    <label className="strong" htmlFor="clim-mail-to-search">To</label>
                    <div className="toolbar" style={{ margin: '6px 0' }}>
                        {to.length === 0 && <span className="muted">No recipient yet.</span>}
                        {to.map(p => (
                            <span key={p.email} className="mark">
                                {p.name || p.email}
                                <button type="button" className="lnk" onClick={() => remove(p)} aria-label={`Remove ${p.email}`}>Remove</button>
                            </span>
                        ))}
                    </div>
                    <input
                        id="clim-mail-to-search"
                        className="input"
                        type="search"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search colleagues by name or email"
                        autoComplete="off"
                    />
                    {results.length > 0 && (
                        <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, border: '1px solid var(--hair)', borderRadius: 'var(--ctl-radius)' }}>
                            {results.map(p => (
                                <li key={p.email}>
                                    <button type="button" className="menu-item" onClick={() => add(p)}>
                                        {p.name} <span className="muted">{p.email}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div>
                    <label className="strong" htmlFor="clim-mail-subject">Subject</label>
                    <input id="clim-mail-subject" className="input" value={subject} onChange={e => setSubject(e.target.value)} />
                </div>

                <div>
                    <label className="strong" htmlFor="clim-mail-intro">Message</label>
                    <textarea
                        id="clim-mail-intro"
                        className="input"
                        style={{ height: 'auto', padding: '8px 12px', resize: 'vertical' }}
                        rows={6}
                        value={intro}
                        onChange={e => setIntro(e.target.value)}
                    />
                    <p className="muted" style={{ margin: '4px 0 0' }}>The key figures, the comments and the footer are added automatically.</p>
                </div>
            </div>
        </OverviewDialog>
    );
}
